import React, { useEffect, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Color from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";

interface KeyInsightMiniEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const DEFAULT_TEXT_COLOR = "#101828";

const normalizeLinkUrl = (url: string) => {
  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return "";
  }

  if (/^(https?:|mailto:|tel:)/i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  return `https://${trimmedUrl}`;
};

const MiniToolbarButton = ({
  active = false,
  label,
  onClick,
  children,
}: {
  active?: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    aria-label={label}
    className={`key-insight-toolbar-button ${active ? "active" : ""}`}
    data-tooltip={label}
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
    type="button"
  >
    {children}
  </button>
);

const KeyInsightMiniEditor = ({
  value,
  onChange,
  placeholder = "Write a key insight...",
}: KeyInsightMiniEditorProps) => {
  const colorInputRef = useRef<HTMLInputElement | null>(null);
  const linkPopoverRef = useRef<HTMLDivElement | null>(null);
  const [toolbarVersion, setToolbarVersion] = useState(0);
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        blockquote: false,
        bulletList: false,
        code: false,
        codeBlock: false,
        hardBreak: false,
        heading: false,
        horizontalRule: false,
        listItem: false,
        orderedList: false,
      }),
      Underline,
      TextStyle,
      Color,
      Link.configure({ openOnClick: false }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        "aria-label": placeholder,
      },
    },
    onSelectionUpdate: () => setToolbarVersion((version) => version + 1),
    onTransaction: () => setToolbarVersion((version) => version + 1),
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    if (!isLinkPopoverOpen) return;

    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (
        linkPopoverRef.current &&
        !linkPopoverRef.current.contains(event.target as Node)
      ) {
        setIsLinkPopoverOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);

    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [isLinkPopoverOpen]);

  const textStyleAttributes = editor?.getAttributes("textStyle") || {};
  const selectedColor =
    typeof textStyleAttributes.color === "string"
      ? textStyleAttributes.color
      : DEFAULT_TEXT_COLOR;

  const openLinkPopover = () => {
    setLinkUrl(editor?.getAttributes("link").href || "");
    setIsLinkPopoverOpen((isOpen) => !isOpen);
  };

  const handleInsertLink = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedUrl = normalizeLinkUrl(linkUrl);

    if (!normalizedUrl) {
      return;
    }

    editor
      ?.chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: normalizedUrl })
      .run();
    setIsLinkPopoverOpen(false);
  };

  const handleRemoveLink = () => {
    editor?.chain().focus().extendMarkRange("link").unsetLink().run();
    setIsLinkPopoverOpen(false);
    setLinkUrl("");
  };

  return (
    <div className="key-insight-mini-editor">
      <div className="key-insight-mini-toolbar">
        <MiniToolbarButton
          active={!!editor?.isActive("bold")}
          label="Bold"
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          B
        </MiniToolbarButton>
        <MiniToolbarButton
          active={!!editor?.isActive("italic")}
          label="Italic"
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <em>I</em>
        </MiniToolbarButton>
        <MiniToolbarButton
          active={!!editor?.isActive("underline")}
          label="Underline"
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
        >
          <span className="key-insight-underline-icon">U</span>
        </MiniToolbarButton>
        <MiniToolbarButton
          active={!!editor?.isActive("strike")}
          label="Strikethrough"
          onClick={() => editor?.chain().focus().toggleStrike().run()}
        >
          <span className="key-insight-strike-icon">S</span>
        </MiniToolbarButton>
        <div className="key-insight-color-wrap">
          <MiniToolbarButton
            active={!!textStyleAttributes.color}
            label="Text color"
            onClick={() => colorInputRef.current?.click()}
          >
            <span
              className="key-insight-color-dot"
              style={{ background: selectedColor }}
            />
          </MiniToolbarButton>
          <input
            ref={colorInputRef}
            type="color"
            aria-label="Pick text color"
            className="key-insight-color-input"
            value={selectedColor}
            onChange={(event) =>
              editor?.chain().focus().setColor(event.target.value).run()
            }
          />
        </div>
        <div className="key-insight-link-wrap" ref={linkPopoverRef}>
          <MiniToolbarButton
            active={!!editor?.isActive("link")}
            label="Add link"
            onClick={openLinkPopover}
          >
            Link
          </MiniToolbarButton>
          {isLinkPopoverOpen && (
            <form
              className="key-insight-link-popover"
              onSubmit={handleInsertLink}
            >
              <input
                aria-label="Link URL"
                autoFocus
                onChange={(event) => setLinkUrl(event.target.value)}
                placeholder="https://example.com"
                type="text"
                value={linkUrl}
              />
              <div className="key-insight-link-actions">
                {editor?.isActive("link") && (
                  <button onClick={handleRemoveLink} type="button">
                    Remove
                  </button>
                )}
                <button type="submit">Add</button>
              </div>
            </form>
          )}
        </div>
      </div>
      <EditorContent editor={editor} className="key-insight-editor-content" />
    </div>
  );
};

export default KeyInsightMiniEditor;
